import React, { useEffect, useState } from 'react';
import FracSimPageCore from './FracSimPageCore';
import StepFrictionPanel from './fracsim/StepFrictionPanel';
import type { FracSimulation as Simulation, PressureCalibrationResult, StageResult } from './fracsim/types';
import { fractureAPI } from '../services/api';

type PressureStage = StageResult & { pressure_calibration?: PressureCalibrationResult };
const unwrap = (value: unknown): Simulation[] => {
  if (Array.isArray(value)) return value as Simulation[];
  const rows = (value as { results?: unknown })?.results;
  return Array.isArray(rows) ? rows as Simulation[] : [];
};
const firstPressure = (simulation?: Simulation) =>
  (simulation?.results?.[0] as PressureStage | undefined)?.pressure_calibration;

/** Keeps pressure data available to friction interpretation without rendering an empty calibration block. */
export default function FracSimPage() {
  const [installed, setInstalled] = useState(false);
  const [pressure, setPressure] = useState<PressureCalibrationResult | undefined>();
  const [simulation, setSimulation] = useState<Simulation | undefined>();
  useEffect(() => {
    const originalList = fractureAPI.listSimulations;
    const originalSimulate = fractureAPI.simulate;
    const originalGetSimulation = fractureAPI.getSimulation;
    fractureAPI.listSimulations = (async (...args: Parameters<typeof originalList>) => {
      const response = await originalList(...args);
      const latest = unwrap(response.data)[0]; setSimulation(latest); setPressure(firstPressure(latest));
      return response;
    }) as typeof originalList;
    fractureAPI.simulate = (async (...args: Parameters<typeof originalSimulate>) => {
      const response = await originalSimulate(...args);
      const latest = response.data as Simulation; setSimulation(latest); setPressure(firstPressure(latest));
      return response;
    }) as typeof originalSimulate;
    fractureAPI.getSimulation = (async (...args: Parameters<typeof originalGetSimulation>) => {
      const response = await originalGetSimulation(...args);
      const latest = response.data as Simulation; setSimulation(latest); setPressure(firstPressure(latest));
      return response;
    }) as typeof originalGetSimulation;
    setInstalled(true);
    return () => {
      fractureAPI.listSimulations = originalList;
      fractureAPI.simulate = originalSimulate;
      fractureAPI.getSimulation = originalGetSimulation;
    };
  }, []);
  if (!installed) return null;
  return <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
    <div style={{ flex: 1, minHeight: 0 }}><FracSimPageCore /></div>
    <StepFrictionPanel designId={simulation?.design} stageId={simulation?.results?.[0]?.stage_number} pressure={pressure} />
  </div>;
}
